import { Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { env } from '@/app/config/env';
import { ComplaintMessagesMenu } from '@/features/complaints';
import { CountrySelector, MarketTimeIndicator } from '@/features/country-scope';
import { AlertsMenu } from '@/features/dashboard';

import { AdminBreadcrumbs } from './admin-breadcrumbs';
import { ThemeToggle } from './theme-toggle';
import { UserMenu } from './user-menu';

/**
 * The top bar: sidebar trigger, breadcrumbs, and account controls.
 *
 * Deliberately compact — this is an operational tool, and every pixel spent
 * here is a pixel not spent on the table below it (§21).
 */
export function AdminHeader({ onOpenCommandPalette }: { onOpenCommandPalette: () => void }) {
  return (
    <header className="bg-background/95 supports-[backdrop-filter]:bg-background/75 sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b px-3 backdrop-blur sm:px-4">
      <SidebarTrigger className="size-8" />
      <Separator orientation="vertical" className="mr-1 !h-4" />

      <AdminBreadcrumbs />

      <div className="ms-auto flex items-center gap-1">
        {/* Non-production environments are labelled so nobody edits live data
            believing they are on staging. */}
        {!env.isProduction && (
          <span className="border-status-warning-border bg-status-warning-bg text-status-warning me-1 hidden rounded border px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase sm:inline">
            {env.appEnv}
          </span>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={onOpenCommandPalette}
          className="text-muted-foreground border-input hover:border-input hover:text-foreground h-8 w-auto justify-start gap-2 px-2 text-xs font-normal sm:w-52"
        >
          <Search className="size-3.5" aria-hidden />
          <span className="hidden sm:inline">Search…</span>
          <kbd className="bg-muted text-muted-foreground ms-auto hidden rounded border px-1 font-mono text-[10px] sm:inline">
            ⌘K
          </kbd>
        </Button>

        <ComplaintMessagesMenu />
        <AlertsMenu />

        <CountrySelector />
        <MarketTimeIndicator />
        <ThemeToggle />

        <Separator orientation="vertical" className="mx-1 !h-4" />

        <UserMenu />
      </div>
    </header>
  );
}
